import { map, propOr, range } from 'ramda';
import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BiAddMediaActionTarget, BiAddMediaClickOrigin } from '../../../../constants/Bi';
import { MediaManagerSDKBoardSitesVisibility } from '../../../../constants/BoardSitesVisibility';
import { MediaManagerSDKBoardTeamAvailability } from '../../../../constants/BoardsTeamAvailability';
import { ModalName } from '../../../../constants/ModalName';
import { TeammatesMenu } from '../../../../containers/FilesManager/Panel/BottomParts/Team/TeammatesMenu/TeammatesMenu';
import { useMMGRSettings } from '../../../../utils/hoc/withMMGRSettings';
import { CreateBoardPopUpTarget, useTrackBoardAnalytics } from '../../../../utils/hooks/bi/useTrackBoardAnalytics';
import {
  TrackMediaManagerTeammatesViewClickOrigin,
  useTrackTeamAnalytics,
} from '../../../../utils/hooks/bi/useTrackTeamAnalytics';
import { useBoardThumbnail } from '../../../../utils/hooks/useBoardThumbnail';
import { useModalHandlersAndOptions } from '../../../../utils/hooks/useModal';
import { isUserOwnerOfBoard } from '../../../../utils/media-manager-sdk/boards';
import { createObjectUrl } from '../../../../utils/uploadingFile';
import {
  Button,
  FileInput,
  FormField,
  ImageViewer,
  Input,
  InputArea,
  Text,
  TextButton,
  ToggleSwitch,
  WixIcons,
} from '../../index';
import { Loader } from '../../Loader';
import { CoverUploadStatus } from './constants';
import styles from './EditBoardModal.scss';
import { IOuter } from './EditBoardModal.types';

const getBoardRandomCoordinate = (max) => Math.floor(Math.random() * (max + 1));

const defaultThumbnailsColors = ['blue', 'green', 'orange', 'purple', 'yellow'];

const defaultBoardThumbnails = map(
  (colorName) => range(1, 6).map((number) => `${colorName}${number}`),
  defaultThumbnailsColors,
);

const maxBoardCoordinate = defaultBoardThumbnails.length - 1;

let lastBoardPosition = {
  x: getBoardRandomCoordinate(maxBoardCoordinate),
  y: getBoardRandomCoordinate(maxBoardCoordinate),
};

const updateDefaultBoardThumbnailCoords = () => {
  const x = lastBoardPosition.x < maxBoardCoordinate ? lastBoardPosition.x + 1 : 0;
  const y = lastBoardPosition.y < maxBoardCoordinate ? lastBoardPosition.y + 1 : 0;

  lastBoardPosition = {
    x,
    y,
  };
};

const enum BoardModalInput {
  Name = 'name',
  Description = 'description',
}

export interface IBoardState {
  id: string;
  name: string;
  description: string;
  thumbnailUrl: string;
  sitesVisibility: MediaManagerSDKBoardSitesVisibility;
  teamAvailability: MediaManagerSDKBoardTeamAvailability;
  fileThumbnail?: File;
}

const enum ActionType {
  SetInputsValue = 'set-inputs-value',
  SetThumbnailUrl = 'set-thumbnail-url',
  SetSitesVisibility = 'set-sites-visibility',
  SetTeamAvailability = 'set-team-availability',
}

const reducer = (state: IBoardState, action) => {
  switch (action.type) {
    case ActionType.SetInputsValue:
      return { ...state, ...action.payload };

    case ActionType.SetThumbnailUrl:
      return { ...state, fileThumbnail: action.payload, thumbnailUrl: createObjectUrl(action.payload) };

    case ActionType.SetSitesVisibility:
      return { ...state, sitesVisibility: action.payload };

    case ActionType.SetTeamAvailability:
      return { ...state, teamAvailability: action.payload };

    default:
      return state;
  }
};

const getInitialState = (board): IBoardState => {
  const { x, y } = lastBoardPosition;

  return {
    id: propOr('', 'id', board),
    name: propOr('', 'name', board),
    description: propOr('', 'description', board),
    thumbnailUrl: propOr(defaultBoardThumbnails[x][y], 'thumbnailUrl', board),
    sitesVisibility: propOr(MediaManagerSDKBoardSitesVisibility.All, 'sitesVisibility', board),
    teamAvailability: propOr(MediaManagerSDKBoardTeamAvailability.Off, 'teamAvailability', board),
  };
};

export const EditBoardModal: FC<IOuter> = ({ board, onSubmit, closeModal, onCancel, selectedItems }) => {
  const { mmgrSettings } = useMMGRSettings();
  const { formatMessage } = useIntl();
  const { setCloseParams } = useModalHandlersAndOptions(ModalName.EditBoard);

  const { sitesNumber, team } = mmgrSettings;

  const { trackCreateBoardPopUpClick } = useTrackBoardAnalytics();

  const initialState = getInitialState(board);
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    setCloseParams(state);
  }, [state, setCloseParams]);

  useEffect(() => {
    updateDefaultBoardThumbnailCoords();
  }, []);

  const [coverUploadingProgress, setUploadingProgress] = useState(CoverUploadStatus.NOT_STARTED);

  const isCoverUploading = coverUploadingProgress === CoverUploadStatus.INPROGRESS;

  const { thumbnailUrl } = useBoardThumbnail(state);

  const [name, setBoardName] = useState(initialState.name);
  const [description, setBoardDescription] = useState(initialState.description);

  const createInputChangeHandler = (inputName) => (event) => {
    const inputValue = event.target.value;
    inputName === BoardModalInput.Name ? setBoardName(inputValue) : setBoardDescription(inputValue);
  };

  const handleNameChange = useCallback(createInputChangeHandler(BoardModalInput.Name), []);
  const handleDescriptionChange = useCallback(createInputChangeHandler(BoardModalInput.Description), []);

  const handleInputBlur = useCallback(() => {
    dispatch({
      type: ActionType.SetInputsValue,
      payload: { name, description },
    });
  }, [name, description]);

  const handleSitesVisibility = useCallback((event) => {
    dispatch({
      type: ActionType.SetSitesVisibility,
      payload: event.target.checked
        ? MediaManagerSDKBoardSitesVisibility.All
        : MediaManagerSDKBoardSitesVisibility.Current,
    });
  }, []);

  const handleTeamAvailability = useCallback((event) => {
    dispatch({
      type: ActionType.SetTeamAvailability,
      payload: event.target.checked
        ? MediaManagerSDKBoardTeamAvailability.On
        : MediaManagerSDKBoardTeamAvailability.Off,
    });
  }, []);

  const handleFiles = useCallback((files) => {
    if (files[0]) {
      dispatch({
        type: ActionType.SetThumbnailUrl,
        payload: files[0],
      });
    }
  }, []);

  const handleFormSubmit = useCallback(
    (event) => {
      event.preventDefault();
      setUploadingProgress(CoverUploadStatus.INPROGRESS);
      onSubmit(board, { ...state, name, description }, selectedItems)
        .then(() => {
          setUploadingProgress(CoverUploadStatus.SUCCESS);
          closeModal();
        })
        .catch(() => {
          setUploadingProgress(CoverUploadStatus.ERROR);
        });
    },
    [board, state, name, description, onSubmit, closeModal],
  );

  const shouldShowTeamInfo = Boolean(team);
  const isOwnerOfTheBoard = !board || isUserOwnerOfBoard(board);
  const shouldDisableBoardInfoEdit = shouldShowTeamInfo && !isOwnerOfTheBoard;

  const { trackMediaManagerTeammatesViewClick } = useTrackTeamAnalytics();

  const handleViewTeamClick = useCallback(() => {
    trackMediaManagerTeammatesViewClick(board, {
      clickOrigin: TrackMediaManagerTeammatesViewClickOrigin.CreateNewBoard,
    });
  }, [trackMediaManagerTeammatesViewClick]);

  return (
    <div className={styles.modal}>
      <form onSubmit={handleFormSubmit}>
        <div className={styles.formBody}>
          <div className={styles.coverImageHolder}>
            <FileInput
              onFiles={handleFiles}
              biOrigin={BiAddMediaClickOrigin.ImportFilesTabs}
              biTarget={BiAddMediaActionTarget.Upload}
            >
              {({ openFilesInput }) => (
                <>
                  <ImageViewer
                    className={styles.boardImageViewer}
                    dataHook="board-modal-cover-image"
                    imageUrl={thumbnailUrl}
                    showRemoveButton={false}
                    showUpdateButton={!shouldDisableBoardInfoEdit}
                    updateImageInfo={formatMessage({ id: 'boards.modal.update-image' })}
                    onAddImage={(event) => {
                      trackCreateBoardPopUpClick(CreateBoardPopUpTarget.Cover);
                      openFilesInput(event);
                    }}
                    onUpdateImage={(event) => {
                      trackCreateBoardPopUpClick(CreateBoardPopUpTarget.Cover);
                      openFilesInput(event);
                    }}
                    width={208}
                    height={180}
                    disabled={shouldDisableBoardInfoEdit}
                  />

                  {!isCoverUploading && !shouldDisableBoardInfoEdit && (
                    <TextButton
                      dataHook="board-modal-cover-image-replace-button"
                      className={styles.replaceCoverImageButton}
                      size="small"
                      suffixIcon={<WixIcons.Replace />}
                      onClick={(event) => {
                        trackCreateBoardPopUpClick(CreateBoardPopUpTarget.Cover);
                        openFilesInput(event);
                      }}
                    >
                      <FormattedMessage id="boards.modal.replace-cover-image.button" />
                    </TextButton>
                  )}
                </>
              )}
            </FileInput>
          </div>

          <div className={styles.inputsHolder}>
            <div className={styles.inputNameHolder}>
              <FormField label={formatMessage({ id: 'boards.modal.input.name.label' })}>
                <Input
                  className={styles.boardName}
                  placeholder={formatMessage({ id: 'boards.modal.input.name.placeholder' })}
                  onChange={handleNameChange}
                  value={name}
                  onBlur={handleInputBlur}
                  autoFocus
                  dataHook="board-modal-name-input"
                  disabled={shouldDisableBoardInfoEdit}
                />
              </FormField>
            </div>
            <FormField label={formatMessage({ id: 'boards.modal.input.description.label' })}>
              <InputArea
                placeholder={formatMessage({ id: 'boards.modal.input.description.placeholder' })}
                minHeight="70px"
                onChange={handleDescriptionChange}
                value={description}
                onBlur={handleInputBlur}
                dataHook="board-modal-description-input"
                disabled={shouldDisableBoardInfoEdit}
              />
            </FormField>

            {shouldShowTeamInfo && (
              <div>
                <Text className={styles.teamAccess} tagName="div">
                  <FormattedMessage id="boards.teams.team-access" />
                </Text>
                <div>
                  <FormField
                    infoContent={
                      isOwnerOfTheBoard ? (
                        <FormattedMessage id="boards.teams.give-all-teammates-access" />
                      ) : (
                        <FormattedMessage id="boards.teams.only-the-board-creator-cat-toggle-share" />
                      )
                    }
                    label={<FormattedMessage id="boards.teams.share-this-board-with-your-teammates" />}
                    labelPlacement="right"
                    stretchContent={false}
                    dataHook="toggle-team-availability-label"
                  >
                    <ToggleSwitch
                      size="small"
                      checked={state.teamAvailability === MediaManagerSDKBoardTeamAvailability.On}
                      onChange={handleTeamAvailability}
                      dataHook="board-modal-toggle-team-availability"
                      disabled={!isOwnerOfTheBoard}
                    />
                  </FormField>
                </div>
                <Text size="tiny" className={styles.viewTeamSuffixLabel}>
                  <FormattedMessage id="boards.teams.see-who-has-access-to-this-board" />
                </Text>
                <div className={styles.viewTeamContainer}>
                  <TeammatesMenu board={board} team={team}>
                    <Text size="tiny" className={styles.viewTeam} onClick={handleViewTeamClick} dataHook="view-team">
                      <FormattedMessage id="boards.teams.view-team" />
                    </Text>
                  </TeammatesMenu>
                </div>
              </div>
            )}

            {sitesNumber > 1 && !shouldShowTeamInfo && (
              <div className={styles.toggleHolder}>
                <FormField
                  infoContent={formatMessage({ id: 'boards.modal.toggle.tooltip' })}
                  label={formatMessage({ id: 'boards.modal.toggle.label' })}
                  labelPlacement="right"
                  stretchContent={false}
                  dataHook="toggle-sites-visibility-label"
                >
                  <ToggleSwitch
                    size="small"
                    checked={state.sitesVisibility === MediaManagerSDKBoardSitesVisibility.All}
                    onChange={handleSitesVisibility}
                    dataHook="board-modal-toggle-visibility"
                  />
                </FormField>
              </div>
            )}
          </div>
        </div>
        <div className={styles.formFooter}>
          <TextButton
            className={styles.cancelButton}
            onClick={onCancel}
            disabled={isCoverUploading}
            priority="secondary"
            size="small"
            dataHook="edit-board-modal-cancel-button"
          >
            <FormattedMessage id="boards.modal.cancel" />
          </TextButton>

          <Button
            type="submit"
            size="small"
            dataHook="edit-board-modal-submit-button"
            disabled={isCoverUploading || shouldDisableBoardInfoEdit}
          >
            {isCoverUploading && (
              <div className={styles.loader}>
                <Loader size="tiny" />
              </div>
            )}
            {board ? (
              <FormattedMessage id="boards.modal.edit.confirm" />
            ) : (
              <FormattedMessage id="boards.modal.create-new.confirm" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
